'use client';

import Link from 'next/link';
import { useState } from 'react';
import type { DocumentSearchResult, FileEntryKind } from 'pc-types';
import { DeckDocIcon, SheetDocIcon } from '@/components/icons/doc-icons';
import { getFileIcon } from '@/components/spaces/files/entry-list';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  documentSearchHref,
  useDocumentSearch,
} from '@/lib/use-document-search';

export function DocumentSearchIcon({
  result,
}: {
  result: Pick<DocumentSearchResult, 'kind' | 'fileType'>;
}) {
  if (result.kind === 'deck') {
    return <DeckDocIcon className="size-4 shrink-0 text-doc-deck" />;
  }
  if (result.kind === 'workbook') {
    return <SheetDocIcon className="size-4 shrink-0 text-doc-sheet" />;
  }
  return getFileIcon(result.fileType ?? '');
}

const PAGE_SIZE = 20;
// The search endpoint's cap.
const MAX_RESULTS = 100;

function escapeRegExp(text: string) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Bolds the words in the snippet that start with a query word. */
export function HighlightedSnippet({
  text,
  query,
}: {
  text: string;
  query: string;
}) {
  const words = query
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word.length >= 2)
    .map(escapeRegExp);
  if (words.length === 0) return <>{text}</>;
  // Word starts only, matching how search treats each word as a prefix:
  // "ten" marks "tenure", not the middle of "retention".
  const pattern = new RegExp(`(?<![\\p{L}\\p{N}])(${words.join('|')})`, 'giu');
  return (
    <>
      {text.split(pattern).map((part, index) =>
        index % 2 === 1 ? (
          <span key={index} className="font-medium text-fg">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
}

/**
 * The "Found inside files" section under the Documents list: one row per
 * document, each with its best passages linking straight to the page, slide,
 * or sheet they came from.
 */
export function DocumentSearchResults({
  query,
  spaceId,
  kind,
}: {
  query: string;
  spaceId: string | null;
  kind: FileEntryKind | null;
}) {
  // "Show more" grows the list; a new query starts over at one page.
  const searchKey = `${spaceId ?? 'all'}:${kind ?? 'any'}:${query.trim()}`;
  const [shown, setShown] = useState({ key: searchKey, limit: PAGE_SIZE });
  const limit = shown.key === searchKey ? shown.limit : PAGE_SIZE;
  const search = useDocumentSearch(query, { spaceId, kind, limit });
  if (!search.active) return null;
  const mayHaveMore = search.results.length >= limit && limit < MAX_RESULTS;

  return (
    <section className="border-t border-line px-4 pb-6 pt-4">
      <div className="mb-2 flex items-center gap-2 text-xs font-medium text-fg-tertiary">
        <span>Found inside files</span>
      </div>

      {search.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : search.isError ? (
        <p className="text-base text-fg-tertiary">
          Search isn&apos;t working right now. Try again in a moment.
        </p>
      ) : search.results.length === 0 ? (
        <p className="text-base text-fg-tertiary">
          {search.isSearching
            ? 'Searching…'
            : `Nothing inside your files matches “${query.trim()}”.`}
        </p>
      ) : (
        <ul className="space-y-1">
          {search.results.map((result) => (
            <li key={result.documentId}>
              <Link
                href={documentSearchHref(result, result.passages[0])}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-wash-light"
              >
                <DocumentSearchIcon result={result} />
                <span className="truncate text-base font-medium text-fg">
                  {result.name}
                </span>
                {!spaceId && (
                  <span className="ml-auto shrink-0 truncate text-sm text-fg-tertiary">
                    {result.spaceName}
                  </span>
                )}
              </Link>
              {result.passages.map((passage, index) => (
                <Link
                  key={index}
                  href={documentSearchHref(result, passage)}
                  className="ml-6 flex gap-3 rounded-md px-2 py-1 transition-colors hover:bg-wash-light"
                >
                  {passage.label && (
                    <span className="w-40 shrink-0 truncate text-sm text-fg-tertiary">
                      {passage.label}
                    </span>
                  )}
                  <span className="line-clamp-2 text-sm text-fg-secondary">
                    <HighlightedSnippet text={passage.snippet} query={query} />
                  </span>
                </Link>
              ))}
            </li>
          ))}
        </ul>
      )}
      {mayHaveMore && (
        <Button
          variant="tertiary"
          size="sm"
          className="mt-2"
          disabled={search.isSearching}
          onClick={() =>
            setShown({
              key: searchKey,
              limit: Math.min(limit + PAGE_SIZE, MAX_RESULTS),
            })
          }
        >
          Show more
        </Button>
      )}
    </section>
  );
}
